import type { ProgressStepWorkflowState } from "@kontextwerk/web-sdk/core/navigation/progressSteps"
import type {
  OrganizationOnboardingResponse,
  OrganizationOnboardingStep,
} from "../../../../../../../../../../../../../api/kehrblatt/organization-onboarding"

export const resolveOnboardingActiveIndex = (response: OrganizationOnboardingResponse | null) => {
  if (!response?.steps?.length) return 0

  const activeKey = response.active_step_key ?? null

  if (!activeKey) return 0

  const index = response.steps.findIndex((step) => step.key === activeKey)

  return index >= 0 ? index : 0
}

export const onboardingWorkflowState = (
  response: OrganizationOnboardingResponse | null,
  step: OrganizationOnboardingStep,
  index: number,
): ProgressStepWorkflowState => {
  if (step.status === "COMPLETED") return "completed"

  if (step.status === "SKIPPED") return "skipped"

  return index === resolveOnboardingActiveIndex(response) ? "current" : "locked"
}

export const onboardingSummaryDescription = (response: OrganizationOnboardingResponse) => {
  if (response.completed) return "Einrichtung abgeschlossen."

  const total = response.steps.length
  const completed = response.steps.filter((step) => step.status === "COMPLETED").length
  const skipped = response.steps.filter((step) => step.status === "SKIPPED").length
  const activeStep = response.steps[resolveOnboardingActiveIndex(response)] ?? null

  const progress =
    completed === 0
      ? "Noch kein Schritt abgeschlossen."
      : `${completed} von ${total} Schritten abgeschlossen.`
  const skippedText =
    skipped > 0 ? ` ${skipped} ${skipped === 1 ? "Schritt" : "Schritte"} übersprungen.` : ""
  const nextText = activeStep?.status === "OPEN" ? ` Nächster Schritt: ${activeStep.title}.` : ""

  return `${progress}${skippedText}${nextText}`.trim()
}

export const onboardingProgressCounts = (response: OrganizationOnboardingResponse) => {
  const completed = response.steps.filter((step) => step.status === "COMPLETED").length
  const skipped = response.steps.filter((step) => step.status === "SKIPPED").length
  const total = response.steps.length

  return {
    completed,
    skipped,
    open: Math.max(0, total - completed - skipped),
    total,
  }
}

export const onboardingActiveStep = (response: OrganizationOnboardingResponse) =>
  response.steps[resolveOnboardingActiveIndex(response)] ?? null

export const onboardingStatusLabel = (step: OrganizationOnboardingStep) => {
  if (step.status === "COMPLETED") return "Erledigt"

  if (step.status === "SKIPPED") return "Übersprungen"

  return "Offen"
}

export const onboardingStatusColor = (
  step: OrganizationOnboardingStep,
): "green" | "yellow" | "neutral" => {
  if (step.status === "COMPLETED") return "green"

  if (step.status === "SKIPPED") return "yellow"

  return "neutral"
}

export const primaryActionLabel = (step: OrganizationOnboardingStep) => {
  if (step.primary_action === "customer_import") return "Import starten"

  if (step.primary_action === "manage_employees") return "Mitarbeiter verwalten"

  if (step.primary_action === "connect_calendar") return "Kalender verwalten"

  if (step.primary_action === "organization_settings") return "Organisation öffnen"

  if (step.primary_action === "planning_settings") return "Terminplanung öffnen"

  return "Aufträge planen"
}
